import { docNavItems, type DocNavItem } from '@/lib/marketing-content';

export function getApiBaseUrl(): string {
  const apiUrl = (import.meta.env.VITE_API_URL ?? 'http://localhost:4000/api').replace(/\/$/, '');
  return apiUrl;
}

export function getPublicApiBaseUrl(): string {
  return `${getApiBaseUrl()}/v1`;
}

export type DocsNavGroup = {
  title: string;
  items: { slug: string; label: string; description?: string }[];
};

export const docsNavGroups: DocsNavGroup[] = [
  {
    title: 'Getting started',
    items: [
      { slug: 'introduction', label: 'Introduction', description: 'Overview and base URL' },
      { slug: 'quickstart', label: 'Quickstart', description: 'Send your first SMS' },
      { slug: 'authentication', label: 'Authentication', description: 'API keys and scopes' },
    ],
  },
  {
    title: 'Messaging',
    items: [
      { slug: 'send-sms', label: 'Send SMS', description: 'Outbound SMS messages' },
      { slug: 'ussd-sessions', label: 'USSD sessions', description: 'Menu flows and callbacks' },
      { slug: 'voice-calls', label: 'Voice / IVR', description: 'Outbound AT dial + inbound VoiceXML' },
      { slug: 'ivr-studio', label: 'IVR Studio', description: 'Publish flows and Africa\'s Talking callbacks' },
      { slug: 'whatsapp-templates', label: 'WhatsApp templates', description: 'Meta Cloud API template send' },
    ],
  },
  {
    title: 'Platform',
    items: [
      { slug: 'webhooks', label: 'Webhooks', description: 'Signed delivery events' },
      { slug: 'errors', label: 'Errors', description: 'Error codes and retries' },
      { slug: 'sdks', label: 'SDKs', description: 'Official client libraries' },
    ],
  },
];

export function getDocsSidebarItems(): { slug: string; label: string; description?: string }[] {
  return docsNavGroups.flatMap((group) => group.items);
}

export const docsQuickLinks = [
  { slug: 'authentication', title: 'Authentication', body: 'API keys, test vs live environments, and scoped permissions.' },
  { slug: 'send-sms', title: 'Send SMS', body: 'Queue messages with wallet reservation and delivery callbacks.' },
  { slug: 'webhooks', title: 'Webhooks', body: 'Verify signatures and handle delivery notifications.' },
  { slug: 'quickstart', title: 'Quickstart', body: 'Fund your wallet, create a key, and send a test message.' },
];

export const docsSdks = [
  { name: 'JavaScript / TypeScript', package: '@mpange/sdk', runtime: 'Node.js 18+' },
  { name: 'PHP', package: 'mpange/mpange-php', runtime: 'PHP 8.2+' },
  { name: 'Python', package: 'mpange', runtime: 'Python 3.10+' },
];

const topicBySlug = new Map(docNavItems.map((item) => [item.id, item]));

export function getDocTopic(slug: string): DocNavItem | undefined {
  if (slug === 'introduction') return undefined;
  return topicBySlug.get(slug);
}

export function docsPath(slug?: string): string {
  return slug ? `/docs/${slug}` : '/docs';
}

export function docsHashPath(slug?: string): string {
  if (!slug || slug === 'introduction') return '/docs#introduction';
  return `/docs#${slug}`;
}

export function docsHomePath(): string {
  return '/docs';
}

export type DocsTocItem = { id: string; label: string };

export function getTopicToc(topic: DocNavItem): DocsTocItem[] {
  const prefix = `${topic.id}-`;
  return [
    { id: `${prefix}overview`, label: 'Overview' },
    { id: `${prefix}request`, label: 'Request' },
    { id: `${prefix}response`, label: 'Response' },
  ];
}

export function getOverviewToc(): DocsTocItem[] {
  return [
    { id: 'base-url', label: 'Base URL' },
    { id: 'auth-keys', label: 'Authentication' },
    { id: 'quick-start', label: 'Quick start' },
    { id: 'sdk-overview', label: 'SDKs' },
    { id: 'response-format', label: 'Response format' },
    { id: 'idempotency', label: 'Idempotency' },
    { id: 'rate-limits', label: 'Rate limits' },
  ];
}

export function getDocsScrollMarkers(): string[] {
  return [
    'introduction',
    ...getOverviewToc().map((item) => item.id),
    ...docNavItems.flatMap((topic) => [topic.id, ...getTopicToc(topic).map((item) => item.id)]),
  ];
}

export function resolveDocsSidebarSlug(sectionId: string): string {
  if (sectionId === 'introduction' || getOverviewToc().some((item) => item.id === sectionId)) {
    return 'introduction';
  }

  const topic = docNavItems.find(
    (item) => sectionId === item.id || sectionId.startsWith(`${item.id}-`),
  );
  return topic?.id ?? 'introduction';
}

function scrollElementInPanel(
  panel: HTMLElement,
  target: HTMLElement,
  offset = 28,
  behavior: ScrollBehavior = 'smooth',
) {
  const top =
    target.getBoundingClientRect().top -
    panel.getBoundingClientRect().top +
    panel.scrollTop -
    offset;
  panel.scrollTo({ top: Math.max(0, top), behavior });
}

function getMobileDocsScrollOffset(): number {
  const header = document.querySelector<HTMLElement>('.docs-header');
  const mobileNav = document.querySelector<HTMLElement>('.docs-mobile-nav');
  const headerHeight = header?.getBoundingClientRect().height ?? 56;
  const mobileNavHeight = mobileNav?.getBoundingClientRect().height ?? 0;
  return headerHeight + mobileNavHeight + 12;
}

/** Scroll a child horizontally inside an overflow-x track — never the page. */
export function scrollChildIntoHorizontalTrack(child: HTMLElement, track: HTMLElement) {
  const childLeft = child.offsetLeft;
  const childWidth = child.offsetWidth;
  const trackWidth = track.clientWidth;
  const target = childLeft - trackWidth / 2 + childWidth / 2;
  track.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
}

export function scrollToDocsSection(id: string, options?: { behavior?: ScrollBehavior }) {
  const behavior = options?.behavior ?? 'smooth';

  const run = (): boolean => {
    const target = document.getElementById(id);
    if (!target) return false;

    const panel = document.querySelector<HTMLElement>('.docs-layout__scroll');
    const usePanelScroll = panel && window.matchMedia('(min-width: 1024px)').matches;

    window.history.replaceState(null, '', `/docs#${id}`);

    if (usePanelScroll && panel) {
      scrollElementInPanel(panel, target, 28, behavior);
      return true;
    }

    const top = target.getBoundingClientRect().top + window.scrollY - getMobileDocsScrollOffset();
    window.scrollTo({ top: Math.max(0, top), behavior });
    return true;
  };

  if (run()) return;

  let frame = 0;
  const wait = () => {
    if (run()) return;
    frame = window.requestAnimationFrame(wait);
  };
  wait();
}

export const docsResponseExamples = {
  success: `{
  "success": true,
  "data": {
    // Resource payload
  }
}`,
  error: `{
  "success": false,
  "error": {
    "code": "validation_error",
    "message": "Description of what went wrong",
    "details": {}
  }
}`,
};
